// src/rms/pages/Reservations.js
// ============================================================================
// Reservations
// ============================================================================
// Booking respects the vertical's occupancy model: discrete verticals get a
// start and end, continuous ones (storage) book open-ended from a move-in date.
// Double-booking is rejected by the engine, not by hiding the button.
// ============================================================================

import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useRms } from "../context/RmsContext";
import { useCollection, indexById } from "../hooks/useCollection";
import rentalService from "../data/rentalService";
import db from "../data";
import { applyTax } from "../pricing/taxEngine";
import { loadTaxConfig } from "../data/taxService";
import availability from "../pricing/availability";
import ReservationEditor from "../components/ReservationEditor";
import { PageHeader, StatusPill, Modal, Field, Select, Banner, EmptyState, formatDateTime, formatDate, AssetCell, DemoPill } from "../components/ui";
import adEngine from "../ads/adEngine";
import pinService from "../data/pinService";
import { bookingWarnings } from "../pricing/hours";
import { noonInputInDays } from "../utils/datetime";
import PinModal from "../components/PinModal";
import agreementDocumentService from "../data/agreementDocumentService";

/** yyyy-mm-dd in the operator's own day, which is what a date input wants. */
const isoDay = (d) =>
  [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");

function Reservations() {
  const { operator, t, tLower, formatMoney, isContinuous, invalidate, member } = useRms();
  const { rows: reservations } = useCollection("reservations", {}, { orderBy: "starts_at", direction: "desc" });
  const { rows: assets } = useCollection("assets");
  const { rows: customers } = useCollection("customers");
  const { rows: agreements } = useCollection("agreements");
  const { rows: agreementDocs } = useCollection("agreementDocuments");
  const { rows: groups } = useCollection("assetGroups", {}, { orderBy: "position" });
  const { rows: addons } = useCollection("addons", {}, { orderBy: "position" });
  const { rows: rateRules } = useCollection("rateRules", {}, { orderBy: "priority" });
  const { rows: questions } = useCollection("checkoutQuestions", {}, { orderBy: "position" });
  const { rows: blocks } = useCollection("availabilityBlocks");

  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [editing, setEditing] = useState(null);
  // The reservation whose agreement is about to go out, held while the PIN box
  // is open. Sending is outward-facing and cannot be undone, so it is signed.
  const [sendingFor, setSendingFor] = useState(null);
  // The reservation whose documents are open in the panel, and the one document
  // waiting on a PIN before a new link goes out.
  const [docsFor, setDocsFor] = useState(null);
  const [resending, setResending] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  /**
   * The date window, defaulting to the fortnight ahead.
   *
   * A rental desk is asked "what is happening" far more often than "what has
   * ever happened", and an all-time list puts last spring's completed bookings
   * at the top of the screen somebody opened to find this afternoon's pickup.
   * Two weeks is the horizon a counter actually works to.
   *
   * Clearing both boxes shows everything, which is what the history is for.
   */
  const [from, setFrom] = useState(() => isoDay(new Date()));
  const [to, setTo] = useState(() => isoDay(new Date(Date.now() + 14 * 864e5)));

  useEffect(() => {
    if (params.get("new") === "1") {
      // The timeline hands over a unit and a range it has already snapped to
      // whole columns, so a drag across Tuesday and Wednesday opens the form on
      // Tuesday and Wednesday rather than making the operator retype them.
      setPrefill({
        target: params.get("target") || "",
        startsAt: params.get("starts") || "",
        endsAt: params.get("ends") || "",
      });
      setCreating(true);
      params.delete("new");
      params.delete("target");
      params.delete("starts");
      params.delete("ends");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const assetsById = useMemo(() => indexById(assets), [assets]);
  const customersById = useMemo(() => indexById(customers), [customers]);
  // What the customer has signed, or been sent, for each booking. The counter
  // needs this BEFORE they arrive -- somebody who signed at home last night
  // should not be asked to sign again at the desk, and somebody who ignored the
  // email should be spotted before they are standing there.
  const docsByReservation = useMemo(() => {
    const map = {};
    agreementDocs.forEach((d) => {
      if (!d.reservation_id) return;
      (map[d.reservation_id] ||= []).push(d);
    });
    return map;
  }, [agreementDocs]);

  const openAgreementByReservation = useMemo(() => {
    const map = {};
    agreements.filter((a) => a.status === "open").forEach((a) => {
      if (a.reservation_id) map[a.reservation_id] = a;
    });
    return map;
  }, [agreements]);

  const filtered = useMemo(() => {
    // Local midnight to local end-of-day. The inputs give a calendar date and
    // the rows carry timestamptz, so the boundary has to be built in the
    // operator's own day rather than in UTC -- otherwise a morning pickup drops
    // out of "today" for anybody west of Greenwich.
    const start = from ? new Date(`${from}T00:00:00`) : null;
    const end = to ? new Date(`${to}T23:59:59.999`) : null;

    return reservations.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!start && !end) return true;

      // OVERLAP, not start date. A booking that went out last week and comes
      // back on Thursday is happening in the next fortnight, and a filter that
      // hid it would hide the vehicle the desk is waiting for.
      const s0 = new Date(r.starts_at);
      // Continuous verticals leave ends_at null on an open-ended tenancy: that
      // has not finished, so it overlaps every window from its start onwards.
      const e0 = r.ends_at ? new Date(r.ends_at) : null;

      if (end && s0 > end) return false;
      if (start && e0 && e0 < start) return false;
      return true;
    });
  }, [reservations, statusFilter, from, to]);

  /**
   * Holds the filled-in booking while a PIN is collected.
   *
   * Any role may take a booking -- the counter has to be able to, and
   * restricting it was never the ask. The signature answers who, which the row
   * could not otherwise say: created_by holds the account, and at a counter
   * used by a whole shift that is the same answer every time.
   */
  const [pendingBooking, setPendingBooking] = useState(null);

  const handleCreate = async (draft) => {
    adEngine.recordAction();
    setPendingBooking(draft);
  };

  const signAndBook = async (pin) => {
    const signed = await pinService.signAction(operator, {
      pin,
      action: "reservation.create",
      subjectTable: "rms_reservations",
    });
    if (!signed.success) return { error: signed.error };

    const result = await rentalService.createReservation(operator, {
      ...pendingBooking,
      created_signature_id: signed.data,
    });
    // Returned to the modal rather than the page: the booking form is still
    // open behind it, and a refusal that closed the prompt would leave somebody
    // staring at a form with no idea why nothing happened.
    if (!result.success) return { error: result.error };

    setPendingBooking(null);
    setError(null);
    setCreating(false);
    invalidate();
    return {};
  };

  /**
   * Freeze this booking's contract and email a signing link.
   *
   * Rendered here rather than reusing whatever the checkout screen built,
   * because the customer may never reach a checkout screen -- that is the point
   * of sending it. The freeze is the same one: what they open next week is what
   * the contract said today.
   */
  const sendAgreement = async (pin) => {
    const r = sendingFor;
    const asset = assets.find((a) => a.id === r.asset_id) || null;
    const customer = customers.find((c) => c.id === r.customer_id) || null;

    if (!customer) return { error: t("reservations.noCustomer", "That booking has no customer on it.") };

    const rendered = await agreementDocumentService.renderForBooking(operator, {
      customer,
      asset,
      reservation: r,
      location: null,
      quote: r.rate_snapshot,
      formatMoney,
    });
    if (!rendered.success) return { error: rendered.error };
    if (!rendered.data.length) {
      return {
        error: t(
          "reservations.noAgreement",
          "There is no agreement set up yet. An owner adds one in Settings › Agreements."
        ),
      };
    }

    const prepared = await agreementDocumentService.prepareForSignature(operator, rendered.data, {
      customerId: customer.id,
      reservationId: r.id,
      memberId: member?.id,
    });
    if (!prepared.success) return { error: prepared.error };

    // One link per document. A customer with an agreement and a waiver gets two
    // emails, which is worse than one -- but batching them behind a single
    // token would mean one signature covering documents they might not have
    // opened, and that is the thing a waiver exists to be able to prove.
    const links = [];
    for (const doc of prepared.data) {
      const sent = await agreementDocumentService.sendForSignature(operator, doc, { pin });
      if (!sent.success) return { error: sent.error };
      if (sent.data?.link) links.push(sent.data.link);
    }

    setSendingFor(null);
    invalidate();
    setNotice(
      links.length
        ? t("reservations.sentLocal", "No email on this backend — paste this to the customer: {link}", {
            link: links[0],
          })
        : t("reservations.sentAgreement", "Sent to {email} to read and sign.", { email: customer.email })
    );
    return {};
  };

  /**
   * Issue a NEW link for a document that has already gone out.
   *
   * Minting replaces the token, so the previous link stops working the moment
   * this one is issued -- which is exactly what somebody who sent a contract to
   * the wrong address needs, and why this is not simply "send it again".
   */
  const resendAgreement = async (pin) => {
    const sent = await agreementDocumentService.sendForSignature(operator, resending, {
      pin,
      resend: true,
    });
    if (!sent.success) return { error: sent.error };

    setResending(null);
    setDocsFor(null);
    invalidate();
    setNotice(
      sent.data?.link
        ? t("reservations.resentLocal", "No email on this backend — the new link is {link}", { link: sent.data.link })
        : t("reservations.resent", "A new link is on its way. The previous one has stopped working.")
    );
    return {};
  };

  const handleCancel = async (id) => {
    const result = await rentalService.cancelReservation(id);
    if (!result.success) setError(result.error);
    invalidate();
  };

  return (
    <>
      <PageHeader
        title={t("reservationPlural", "Reservations")}
        subtitle={
          isContinuous
            ? "Open-ended leases — each runs until the tenant moves out."
            : t("reservations.subtitle", "{upcoming} upcoming · {out} out now", {
          upcoming: reservations.filter((r) => r.status === "confirmed").length,
          out: reservations.filter((r) => r.status === "checked_out").length,
        })
        }
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => setCreating(true)}>
            {t("reservations.new", "New {booking}", { booking: tLower("reservation", "reservation") })}
          </button>
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div>
        <div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <select className="rms-select" style={{ maxWidth: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">{t("reservations.allStatuses", "All statuses")}</option>
              {["pending", "confirmed", isContinuous ? "active" : "checked_out", "completed", "cancelled"].map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>

            {/* nowrap, and the inputs shrink rather than break. The two halves
                of a range read as one control only while they are on one line;
                wrapping turned "21 Aug to 4 Sep" into two unlabelled date boxes
                stacked on top of each other. The GROUP may still wrap below the
                status select on a narrow screen -- that is fine, it moves as a
                unit. */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                type="date"
                className="rms-input"
                style={{ flex: "1 1 auto", minWidth: 0, maxWidth: 165 }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                aria-label={t("reservations.dateFrom", "From date")}
              />
              <span style={{ color: "var(--rms-muted)", fontSize: 13, flex: "none" }}>
                {t("reservations.dateTo", "to")}
              </span>
              <input
                type="date"
                className="rms-input"
                style={{ flex: "1 1 auto", minWidth: 0, maxWidth: 165 }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                aria-label={t("reservations.dateToLabel", "To date")}
              />
            </div>

            {/* Only offered once a window is actually narrowing the list, so
                the row is not carrying a control that does nothing. */}
            {from || to ? (
              <button
                type="button"
                className="rms-btn rms-btn--ghost rms-btn--sm"
                onClick={() => { setFrom(""); setTo(""); }}
              >
                {t("reservations.allDates", "All dates")}
              </button>
            ) : null}
          </div>

          {reservations.length === 0 ? (
            <EmptyState
              title={t("reservations.empty.title", "No {bookings} yet", {
                bookings: tLower("reservationPlural", "reservations"),
              })}
              message={t("reservations.empty.body", "Book a {item} for a {customer} to get started.", {
                item: tLower("assetSingular", "asset"),
                customer: tLower("customer", "customer"),
              })}
              action={
                <button type="button" className="rms-btn rms-btn--primary" onClick={() => setCreating(true)}>
                  {t("reservations.new", "New {booking}", { booking: tLower("reservation", "reservation") })}
                </button>
              }
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              title={t("reservations.noneInRange", "Nothing in this date range")}
              message={t(
                "reservations.noneInRangeBody",
                "The list opens on the next two weeks. Widen the dates, or show all of them, to see the rest."
              )}
              action={
                <button
                  type="button"
                  className="rms-btn rms-btn--ghost"
                  onClick={() => { setFrom(""); setTo(""); setStatusFilter(""); }}
                >
                  {t("reservations.showEverything", "Show everything")}
                </button>
              }
            />
          ) : (
            <div className="rms-table-wrap">
              <table className="rms-table">
                <thead>
                  <tr>
                    <th>{t("reservations.ref", "Ref")}</th>
                    <th>{t("assetSingular", "Asset")}</th>
                    <th>{t("customer", "Customer")}</th>
                    <th>{isContinuous ? t("occupancy.movedIn", "Moved in") : t("reservations.dates", "Dates")}</th>
                    <th className="rms-num">{t("checkoutFlow.quoted", "Quoted")}</th>
                    <th>{t("campaignForm.status", "Status")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const asset = assetsById[r.asset_id];
                    const customer = customersById[r.customer_id];
                    const agreement = openAgreementByReservation[r.id];
                    return (
                      <tr key={r.id}>
                        <td style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12.5 }}>
                          {r.reservation_number}
                        </td>
                        <td>
                          <AssetCell asset={asset} />
                        </td>
                        <td>
                          {customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : "—"}
                          {r.is_demo ? <> <DemoPill /></> : null}
                        </td>
                        <td style={{ fontSize: 12.5 }}>
                          {isContinuous ? (
                            formatDate(r.starts_at)
                          ) : (
                            <>
                              {formatDateTime(r.starts_at)}
                              <div style={{ color: "var(--rms-muted)" }}>to {formatDateTime(r.ends_at)}</div>
                            </>
                          )}
                        </td>
                        <td className="rms-num">{formatMoney(r.quoted_total_cents)}</td>
                        <td>
                          <StatusPill status={r.status} />
                          {(() => {
                            const docs = docsByReservation[r.id] || [];
                            if (!docs.length) return null;
                            const signed = docs.filter((d) => d.status === "signed");
                            const waiting = docs.filter((d) => ["sent", "viewed"].includes(d.status));
                            return (
                              <div style={{ marginTop: 4, fontSize: 11 }}>
                                {/* The way in to the per-document panel. A row
                                    is too cramped for one control per document,
                                    and this line is already where somebody
                                    looks to find out where the paperwork is. */}
                                <button
                                  type="button"
                                  className="rms-linkish"
                                  style={{ fontSize: 11 }}
                                  onClick={() => setDocsFor(r)}
                                >
                                  {signed.length
                                    ? t("reservations.docsSigned", "{n, plural, one {# signed} other {# signed}}", { n: signed.length })
                                    : null}
                                  {signed.length && waiting.length ? " · " : null}
                                  {waiting.length
                                    ? t("reservations.docsWaiting", "{n, plural, one {# awaiting signature} other {# awaiting signature}}", { n: waiting.length })
                                    : null}
                                </button>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="rms-num" style={{ whiteSpace: "nowrap" }}>
                          {r.status === "confirmed" || r.status === "pending" ? (
                            <>
                              <Link className="rms-btn rms-btn--primary rms-btn--sm" to={`/rms/checkout/${r.id}`}>
                                {t("checkout", "Check out")}
                              </Link>{" "}
                              <button
                                type="button"
                                className="rms-btn rms-btn--ghost rms-btn--sm"
                                onClick={() => setEditing(r)}
                              >
                                {t("action.edit", "Edit")}
                              </button>
                              {/* Before they arrive, not after. The whole
                                  point of a signing link is that the contract
                                  is read somewhere other than a counter with a
                                  queue behind it. */}
                              <button
                                type="button"
                                className="rms-btn rms-btn--ghost rms-btn--sm"
                                onClick={() => setSendingFor(r)}
                              >
                                {t("reservations.sendAgreement", "Send agreement")}
                              </button>
                              <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => handleCancel(r.id)}>
                                {t("action.cancel", "Cancel")}
                              </button>
                            </>
                          ) : agreement ? (
                            <Link className="rms-btn rms-btn--ghost rms-btn--sm" to={`/rms/checkin/${agreement.id}`}>
                              {t("checkin", "Check in")}
                            </Link>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {notice ? <Banner tone="ok">{notice}</Banner> : null}

      {editing ? (
        <ReservationEditor
          reservation={editing}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setNotice(msg); invalidate(); }}
        />
      ) : null}

      {creating ? (
        <ReservationForm
          assets={assets}
          groups={groups}
          addons={addons}
          rateRules={rateRules}
          questions={questions}
          blocks={blocks}
          customers={customers}
          prefill={prefill}
          isContinuous={isContinuous}
          t={t}
          formatMoney={formatMoney}
          onCancel={() => { setCreating(false); setPrefill(null); }}
          onSave={handleCreate}
        />
      ) : null}
      {/* Over the booking form, not instead of it: cancelling returns to the
          dates and the customer they already chose. */}
      {pendingBooking ? (
        <PinModal
          title={t("reservations.signBooking", "Who is taking this booking?")}
          explain={t("reservations.signBookingHelp", "Your PIN goes on the record beside it.")}
          onCancel={() => setPendingBooking(null)}
          onResolved={signAndBook}
        />
      ) : null}

      {docsFor ? (
        <Modal
          title={t("reservations.docsTitle", "Agreements on this booking")}
          onClose={() => setDocsFor(null)}
          width={560}
        >
          {(docsByReservation[docsFor.id] || []).map((d) => {
            const out = ["sent", "viewed"].includes(d.status);
            return (
              <div
                key={d.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: "1px solid var(--rms-border)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{d.title}</div>
                  <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
                    <StatusPill status={d.status} />{" "}
                    {d.status === "signed" && d.signed_at
                      ? t("reservations.docSignedAt", "on {when}", { when: formatDateTime(d.signed_at) })
                      : null}
                    {out && d.last_sent_at
                      ? t("reservations.docSentAt", "link issued {when}", { when: formatDateTime(d.last_sent_at) })
                      : null}
                    {d.resent_count > 0
                      ? ` · ${t("reservations.docResent", "sent again {n, plural, one {# time} other {# times}}", { n: d.resent_count })}`
                      : null}
                  </div>
                </div>

                {d.status === "signed" ? (
                  <Link className="rms-btn rms-btn--ghost rms-btn--sm" to={`/rms/agreement/${d.id}`}>
                    {t("reservations.docOpen", "Open")}
                  </Link>
                ) : out ? (
                  <button
                    type="button"
                    className="rms-btn rms-btn--ghost rms-btn--sm"
                    onClick={() => setResending(d)}
                  >
                    {t("reservations.docResend", "Send a new link")}
                  </button>
                ) : null}
              </div>
            );
          })}

          <p className="rms-help" style={{ marginTop: 12 }}>
            {t(
              "reservations.docResendHelp",
              "Sending a new link stops the previous one working, so a contract that went to the wrong address cannot still be signed from it."
            )}
          </p>
        </Modal>
      ) : null}

      {resending ? (
        <PinModal
          title={t("reservations.signResend", "Who is sending this again?")}
          explain={t("reservations.signResendHelp", "Your PIN goes on the record beside it, and the previous link stops working.")}
          onCancel={() => setResending(null)}
          onResolved={resendAgreement}
        />
      ) : null}

      {sendingFor ? (
        <PinModal
          title={t("reservations.signSend", "Who is sending this agreement?")}
          explain={t("reservations.signSendHelp", "Your PIN goes on the record beside it. An agreement cannot be unsent.")}
          onCancel={() => setSendingFor(null)}
          onResolved={sendAgreement}
        />
      ) : null}

    </>
  );
}

// ----------------------------------------------------------------------------

function ReservationForm({
  assets, groups, addons, rateRules, questions, blocks,
  customers, isContinuous, t, formatMoney, onCancel, onSave, prefill,
}) {
  const { operator, tLower, invalidate } = useRms();
  const [target, setTarget] = useState(prefill?.target || ""); // "asset:<id>" or "group:<id>"
  const [customerId, setCustomerId] = useState("");
  const { rows: operatingHours } = useCollection("operatingHours");
  const { rows: hoursExceptions } = useCollection("hoursExceptions");

  // Noon both ends, so an untouched booking is a whole number of days -- which
  // is what the rate engine charges for and what the customer is quoted. A
  // prefill from the timeline is a real selection and is left alone.
  const [startsAt, setStartsAt] = useState(prefill?.startsAt || noonInputInDays(0));
  const [endsAt, setEndsAt] = useState(prefill?.endsAt || noonInputInDays(3));
  const [addonQty, setAddonQty] = useState({});

  /**
   * The operator's tax setup, loaded once so the quote can show what will
   * actually be charged. The modal used to label subtotal-plus-addons as
   * "Total", which is only true for an operator with no tax configured.
   */
  const [taxConfig, setTaxConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadTaxConfig(operator).then((c) => { if (!cancelled) setTaxConfig(c); });
    return () => { cancelled = true; };
  }, [operator]);

  const [creatingCustomer, setCreatingCustomer] = useState(false);

  /**
   * Creates a customer from a typed name and nothing else.
   *
   * A booking taken over the phone starts with a name. Refusing to hold the
   * vehicle until somebody has an email address is how a rental gets written
   * on paper instead, and the paper never reaches the system.
   *
   * The record is marked provisional rather than left looking finished, so a
   * later screen can ask for the rest and checkout can insist on it.
   */
  const createCustomer = async (typed) => {
    const name = typed.trim();
    if (!name) return null;
    setCreatingCustomer(true);
    const [first, ...rest] = name.split(/\s+/);
    const created = await db.create("customers", {
      operator_id: operator.id,
      first_name: first,
      last_name: rest.join(" ") || null,
      is_provisional: true,
    });
    setCreatingCustomer(false);
    if (!created.success) {
      setError(created.error);
      return null;
    }
    invalidate();
    return created.data;
  };

  const picked = customers.find((c) => c.id === customerId);
  const customerHelp = picked?.is_provisional
    ? t("reservations.provisionalCustomer", "New {customer} — only a name so far. The rest can be filled in before checkout.", {
        customer: tLower("customer", "customer"),
      })
    : null;

  /** Answers to the fields a question reveals: { [questionId]: { [key]: value } } */
  const [followUps, setFollowUps] = useState({});
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState(null);

  const startIso = new Date(startsAt).toISOString();
  const endIso = isContinuous ? null : new Date(endsAt).toISOString();

  // Only the reservations touching the dates on screen, not the operator's
  // whole history. The page's table still loads everything -- it is a history
  // view and that is its job -- but availability only ever asks about a window,
  // and this is the query that runs again on every keystroke in a date field.
  //
  // Padded by the largest turnaround in the fleet, because a booking that ends
  // just before the window can still block its opening hours.
  const maxTurnaround = useMemo(
    () => assets.reduce((n, a) => Math.max(n, a.turnaround_minutes || 0), 0),
    [assets]
  );
  const windowWhere = useMemo(
    () => availability.overlapWindow({ startsAt: startIso, endsAt: endIso, paddingMinutes: maxTurnaround }),
    [startIso, endIso, maxTurnaround]
  );
  const { rows: reservations } = useCollection("reservations", windowWhere);

  // Bucketed once per render rather than rescanned per unit: a fifty-unit pool
  // was fifty full passes over that array.
  const reservationsByAsset = useMemo(() => availability.byAsset(reservations), [reservations]);

  // Assets that belong to a group are booked through the group, not directly —
  // otherwise staff would have to know which physical unit is free.
  const groupedIds = new Set(assets.filter((a) => a.group_id).map((a) => a.id));
  const standalone = assets.filter(
    (a) => !groupedIds.has(a.id) && !["out_of_service", "retired"].includes(a.status)
  );

  const [kind, id] = target.split(":");
  const group = kind === "group" ? groups.find((g) => g.id === id) : null;
  const groupAssets = group ? assets.filter((a) => a.group_id === group.id) : [];

  // For a group, price against a representative member.
  const asset = kind === "asset" ? assets.find((a) => a.id === id) : groupAssets[0] || null;

  const groupFree = useMemo(() => {
    if (!group) return null;
    return rentalService.availability.groupAvailability({
      groupAssets, reservations, blocks, startsAt: startIso, endsAt: endIso,
      reservationsByAsset,
    });
  }, [group, groupAssets, reservations, reservationsByAsset, blocks, startIso, endIso]);

  const conflict = useMemo(() => {
    if (kind !== "asset" || !asset) return null;
    return rentalService.availability.availabilityConflict({
      asset,
      reservations: availability.reservationsFor(reservationsByAsset, asset.id),
      blocks, startsAt: startIso, endsAt: endIso,
    });
  }, [kind, asset, reservationsByAsset, blocks, startIso, endIso]);

  const utilization = useMemo(
    () => rentalService.availability.utilizationPct({
      assets, reservations, startsAt: startIso, endsAt: endIso, reservationsByAsset,
    }),
    [assets, reservations, reservationsByAsset, startIso, endIso]
  );

  const quote = useMemo(() => {
    if (!asset) return null;
    return rentalService.quoteReservationSync({
      asset, group, rules: rateRules, startsAt: startIso, endsAt: endIso, utilization,
    });
  }, [asset, group, rateRules, startIso, endIso, utilization]);

  const availableAddons = useMemo(
    () => (asset ? rentalService.rateEngine.addonsForAsset(addons, asset, group) : []),
    [addons, asset, group]
  );

  const relevantQuestions = useMemo(
    () =>
      (questions || []).filter(
        (q) =>
          q.is_active !== false &&
          (!q.asset_id || q.asset_id === asset?.id) &&
          (!q.asset_group_id || q.asset_group_id === group?.id)
      ),
    [questions, asset, group]
  );

  /** Has this question's answer reached the value that fires its consequences? */
  const isTriggered = (q) => !!q.trigger_value && answers[q.id] === q.trigger_value;

  /**
   * Keep the triggered addons in step with the answers.
   *
   * Written as a reconciliation over every question with a consequence rather
   * than as a handler on the checkbox, so that untick removes the charge as
   * reliably as tick adds it. A one-way handler is how you end up billing
   * somebody for an additional driver they took back.
   *
   * Quantity comes from a follow-up field marked setsAddonQuantity when there
   * is one -- three children need three child seats, not one -- and otherwise
   * from the addon's own default.
   */
  useEffect(() => {
    const withConsequence = relevantQuestions.filter((q) => q.triggers_addon_id);
    if (!withConsequence.length) return;

    setAddonQty((current) => {
      let changed = false;
      const next = { ...current };

      withConsequence.forEach((q) => {
        const addon = availableAddons.find((a) => a.id === q.triggers_addon_id);
        if (!addon) return;

        let want = 0;
        if (isTriggered(q)) {
          const counter = (q.follow_up_fields || []).find((f) => f.setsAddonQuantity);
          const typed = counter ? Number(followUps[q.id]?.[counter.key]) : NaN;
          want = Number.isFinite(typed) && typed > 0 ? typed : addon.default_quantity || 1;
          if (addon.max_quantity) want = Math.min(want, addon.max_quantity);
        }

        if ((next[addon.id] || 0) !== want) {
          next[addon.id] = want;
          changed = true;
        }
      });

      return changed ? next : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, followUps, relevantQuestions, availableAddons]);

  const addonTotal = availableAddons.reduce((sum, a) => {
    const qty = a.is_required ? addonQty[a.id] ?? a.default_quantity ?? 1 : addonQty[a.id] || 0;
    return qty > 0 ? sum + rentalService.rateEngine.addonAmountCents(a, qty, quote?.units || 1) : sum;
  }, 0);

  const grandTotal = (quote?.subtotal_cents || 0) + addonTotal;

  /**
   * The quote as it will be invoiced: rental plus addons, taxed.
   *
   * Uses the same applyTax the invoice uses rather than a second calculation,
   * because two implementations of tax is how a quote and its invoice come to
   * disagree by a cent and nobody can say which is right.
   */
  const priced = useMemo(() => {
    if (!quote || !taxConfig) return null;
    // applyTax reads tax_group_id off the line and does NOT resolve it from the
    // kind -- the same shape rentalService builds before invoicing. Passing the
    // kind alone yields a silently untaxed quote that then disagrees with the
    // invoice, which is exactly the divergence this preview exists to prevent.
    const taxOn = (kind) => {
      const groupId = taxConfig.groupFor ? taxConfig.groupFor(kind) : null;
      return { tax_group_id: groupId, taxable: groupId !== null };
    };

    const lines = [
      { kind: "rental", description: "rental", quantity: quote.units, amount_cents: quote.subtotal_cents, ...taxOn("rental") },
      ...availableAddons
        .map((a) => {
          const qty = a.is_required ? addonQty[a.id] ?? a.default_quantity ?? 1 : addonQty[a.id] || 0;
          return qty > 0
            ? { kind: "addon", description: a.name, quantity: qty,
                amount_cents: rentalService.rateEngine.addonAmountCents(a, qty, quote.units),
                // The addon's own taxable flag still wins a "no", matching
                // finalizeInvoice: an operator who marked something exempt
                // means it, whatever the category default says.
                ...(a.taxable === false ? { tax_group_id: null, taxable: false } : taxOn("addon")) }
            : null;
        })
        .filter(Boolean),
    ];
    return applyTax(lines, taxConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, taxConfig, availableAddons, addonQty]);

  /**
   * The rate with every rule already in it, from the engine rather than
   * recomputed here -- the subtotal is now derived FROM this figure, so a
   * second calculation on the screen could only ever disagree with it.
   */
  const effectiveUnitCents = quote?.effective_unit_cents || 0;

  /**
   * Pickups and returns that fall when the counter is shut.
   *
   * A WARNING and an acknowledgement, not a refusal. A rental desk genuinely
   * does hand a vehicle over at seven in the morning by arrangement, and
   * software that refuses to record what actually happened is software the
   * counter works around -- usually by booking a lie. So it says plainly what
   * is wrong and asks somebody to mean it.
   *
   * Empty when nothing is configured. An operator who has never opened the
   * Hours screen is open around the clock, which is what the tables mean by
   * having no rows.
   */
  const closedWarnings = useMemo(
    () => bookingWarnings(startsAt, isContinuous ? null : endsAt, {
      weekly: operatingHours,
      exceptions: hoursExceptions,
    }),
    [startsAt, endsAt, isContinuous, operatingHours, hoursExceptions]
  );

  // Reset the moment the dates change: an acknowledgement is about the times
  // that were on screen when it was given, not a permanent opt-out.
  const [acceptedClosed, setAcceptedClosed] = useState(false);
  useEffect(() => { setAcceptedClosed(false); }, [startsAt, endsAt]);

  const closedText = (w) => {
    const when = w.leg === "start"
      ? t("hours.warn.pickup", "The pickup")
      : t("hours.warn.return", "The return");
    if (w.kind === "closedDate") {
      return w.label
        ? t("hours.warn.closedNamed", "{when} falls on {label}, when you are closed.", { when, label: w.label })
        : t("hours.warn.closedDate", "{when} falls on a day you are closed.", { when });
    }
    if (w.kind === "closedDay") {
      return t("hours.warn.closedDay", "{when} falls on a day you are not open.", { when });
    }
    return t("hours.warn.outsideHours", "{when} is outside your opening hours ({opens} to {closes}).", {
      when,
      opens: (w.opensAt || "").slice(0, 5),
      closes: (w.closesAt || "").slice(0, 5),
    });
  };

  const submit = () => {
    if (!target) return setError(`Choose what to book.`);
    if (!customerId) return setError(t("reservations.error.customer", "Choose a {customer}.", {
      customer: tLower("customer", "customer"),
    }));
    if (!isContinuous && new Date(endsAt) <= new Date(startsAt)) {
      return setError("The return date must be after the start date.");
    }
    if (conflict) return setError(conflict.message);
    if (closedWarnings.length && !acceptedClosed) {
      return setError(t("hours.warn.confirm", "Tick the box to book outside your opening hours."));
    }
    if (group && groupFree && groupFree.free === 0) {
      return setError(`No ${group.name} units are free for that window.`);
    }
    // A required follow-up is only required once its question has actually
    // triggered -- an untriggered field is not on screen, and refusing to save
    // over something nobody can see is the worst kind of dead end.
    for (const q of relevantQuestions) {
      if (!isTriggered(q)) continue;
      const missing = (q.follow_up_fields || []).find(
        (f) => f.required && !String(followUps[q.id]?.[f.key] || "").trim()
      );
      if (missing) return setError(`${missing.label} is required.`);
    }
    if (quote && !quote.meets_minimum) {
      return setError(`A minimum of ${quote.minimum_units} ${quote.rate_unit.replace("_", " ")}s applies to those dates.`);
    }
    const missing = relevantQuestions.filter((q) => q.is_required && !answers[q.id]);
    if (missing.length) return setError(`Please answer: ${missing.map((q) => q.prompt).join(", ")}`);

    setError(null);
    onSave({
      asset_id: kind === "asset" ? id : null,
      asset_group_id: kind === "group" ? id : null,
      customer_id: customerId,
      starts_at: startIso,
      ends_at: endIso,
      addons: availableAddons
        .map((a) => ({ addon_id: a.id, quantity: a.is_required ? addonQty[a.id] ?? a.default_quantity ?? 1 : addonQty[a.id] || 0 }))
        .filter((a) => a.quantity > 0),
      answers: relevantQuestions
        .filter((q) => answers[q.id])
        .map((q) => ({
          question_id: q.id,
          prompt: q.prompt,
          answer: String(answers[q.id]),
          follow_up: isTriggered(q) ? followUps[q.id] || {} : {},
        })),
    });
  };

  return (
    <Modal
      title={t("reservations.form.new", "New {booking}", { booking: tLower("reservation", "reservation") })}
      onClose={onCancel}
      width={700}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {t("reservations.create", "Create {booking}", {
            booking: tLower("reservation", "reservation"),
          })}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <Field label={`What are you booking?`} required>
        <Select
          value={target}
          onChange={setTarget}
          placeholder="Choose…"
          options={[
            ...groups.map((g) => ({
              value: `group:${g.id}`,
              label: t("reservations.groupUnits", "{name} — {count, plural, one {# unit} other {# units}}", {
            name: g.name,
            count: assets.filter((a) => a.group_id === g.id).length,
          }),
            })),
            ...standalone.map((a) => ({
              value: `asset:${a.id}`,
              label: `${a.asset_code} — ${a.name}${a.status !== "available" ? ` (${a.status.replace("_", " ")})` : ""}`,
            })),
          ]}
        />
      </Field>

      <Field label={t("customer", "Customer")} required help={customerHelp}>
        <CustomerPicker
          customers={customers}
          value={customerId}
          onPick={(id) => { setCustomerId(id); setError(null); }}
          onCreate={createCustomer}
          creating={creatingCustomer}
          t={t}
          tLower={tLower}
        />
      </Field>

      <div className="rms-field-grid">
        <Field label={isContinuous ? t("reservations.moveInDate", "Move-in date") : t("campaignForm.starts", "Starts")} required>
          <input className="rms-input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </Field>
        {!isContinuous ? (
          <Field label={t("reservations.dueBack", "Due back")} required>
            <input className="rms-input" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </Field>
        ) : null}
      </div>

      {closedWarnings.length ? (
        <Banner tone="warn">
          <div>
            {closedWarnings.map((w) => (
              <div key={w.leg}>{closedText(w)}</div>
            ))}
            <label className="rms-checkline" style={{ marginTop: 8 }}>
              <input
                type="checkbox"
                checked={acceptedClosed}
                onChange={(e) => setAcceptedClosed(e.target.checked)}
              />
              {t("hours.warn.anyway", "Book it anyway — this is arranged.")}
            </label>
          </div>
        </Banner>
      ) : null}

      {groupFree ? (
        <Banner tone={groupFree.free === 0 ? "danger" : groupFree.free <= 1 ? "warn" : "ok"}>
          <strong>{groupFree.free} of {groupFree.total}</strong> {group.name} units free for those
          dates. We'll assign a specific one automatically.
        </Banner>
      ) : null}

      {conflict ? <Banner tone={conflict.kind === "turnaround" ? "warn" : "danger"}>{conflict.message}</Banner> : null}

      {isContinuous ? (
        <Banner tone="info">
          {t("reservations.storageLeasesAreOpen", "Storage leases are open-ended. Billing runs monthly until the tenant gives notice and moves out.")}
        </Banner>
      ) : null}

      {availableAddons.length ? (
        <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 6 }}>
          <h3 className="rms-section-title">{t("reservations.addOns", "Add-ons")}</h3>
          {availableAddons.filter((a) => !a.is_hidden).map((a) => {
            const qty = a.is_required ? addonQty[a.id] ?? a.default_quantity ?? 1 : addonQty[a.id] || 0;
            return (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--rms-border)" }}>
                <input
                  type="checkbox"
                  checked={qty > 0}
                  disabled={a.is_required}
                  onChange={(e) => setAddonQty((s) => ({ ...s, [a.id]: e.target.checked ? a.default_quantity || 1 : 0 }))}
                />
                <span style={{ flex: 1, fontSize: 13.5 }}>
                  {a.name}
                  {a.is_required ? <span className="rms-pill rms-pill--neutral" style={{ marginInlineStart: 6 }}>required</span> : null}
                  <span style={{ color: "var(--rms-muted)", marginInlineStart: 6 }}>
                    {formatMoney(a.price_cents)} {a.charge_type.replace(/_/g, " ")}
                  </span>
                </span>
                {qty > 0 && a.max_quantity !== 1 ? (
                  <input
                    className="rms-input"
                    style={{ width: 68 }}
                    type="number"
                    min="1"
                    max={a.max_quantity || undefined}
                    value={qty}
                    onChange={(e) => setAddonQty((s) => ({ ...s, [a.id]: Number(e.target.value) }))}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {relevantQuestions.length ? (
        <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 12 }}>
          <h3 className="rms-section-title">{t("reservations.bookingQuestions", "Booking questions")}</h3>

          {/* One row rather than a stack. These are short answers -- a yes/no, a
              pick from four, a flight number -- and giving each the full width of
              the dialog pushed the price and the Create button below the fold on
              a laptop. auto-fit rather than a fixed three columns because the
              question set is per-vertical: a costume hire asks two, construction
              asks five, and neither should have to know about this rule. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: "0 14px",
              // end, not start: prompts are operator-written and differ in
              // length, so a two-line one pushed its input a line below its
              // neighbours. Aligning on the controls puts them on one line and
              // lets the labels be ragged above, which is the readable way
              // round -- the eye follows the row of boxes, not the text.
              alignItems: "end",
            }}
          >
          {relevantQuestions.map((q) => (
            <Field key={q.id} label={q.prompt} required={q.is_required} help={q.help_text}>
              {q.question_type === "multiple_choice" ? (
                <Select
                  value={answers[q.id] || ""}
                  onChange={(v) => setAnswers((s) => ({ ...s, [q.id]: v }))}
                  options={q.options || []}
                />
              ) : q.question_type === "long_answer" ? (
                <textarea
                  className="rms-textarea"
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((s) => ({ ...s, [q.id]: e.target.value }))}
                />
              ) : q.question_type === "boolean" ? (
                <label className="rms-checkline">
                  <input
                    type="checkbox"
                    checked={answers[q.id] === "Yes"}
                    onChange={(e) => setAnswers((s) => ({ ...s, [q.id]: e.target.checked ? "Yes" : "No" }))}
                  />
                  {t("common.yes", "Yes")}
                </label>
              ) : (
                <input
                  className="rms-input"
                  type={q.question_type === "number" ? "number" : "text"}
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((s) => ({ ...s, [q.id]: e.target.value }))}
                />
              )}

              {/* Revealed only once the answer matches. Rendered inside the
                  question rather than beside it so the pairing is obvious at a
                  glance, and indented so it reads as a consequence of the
                  answer above rather than as another question. */}
              {isTriggered(q) && (q.follow_up_fields || []).length ? (
                <div
                  style={{
                    marginTop: 8,
                    paddingInlineStart: 10,
                    borderInlineStart: "2px solid var(--rms-border)",
                    display: "grid",
                    gap: 8,
                  }}
                >
                  {q.follow_up_fields.map((f) => (
                    <Field key={f.key} label={f.label} required={f.required}>
                      {f.type === "long_answer" ? (
                        <textarea
                          className="rms-textarea"
                          rows={2}
                          value={followUps[q.id]?.[f.key] || ""}
                          onChange={(e) =>
                            setFollowUps((s) => ({ ...s, [q.id]: { ...s[q.id], [f.key]: e.target.value } }))
                          }
                        />
                      ) : (
                        <input
                          className="rms-input"
                          type={f.type === "number" ? "number" : "text"}
                          min={f.type === "number" ? 1 : undefined}
                          value={followUps[q.id]?.[f.key] || ""}
                          onChange={(e) =>
                            setFollowUps((s) => ({ ...s, [q.id]: { ...s[q.id], [f.key]: e.target.value } }))
                          }
                        />
                      )}
                    </Field>
                  ))}
                </div>
              ) : null}
            </Field>
          ))}
          </div>
        </div>
      ) : null}

      {quote ? (
        <div className="rms-card rms-card-pad" style={{ background: "var(--rms-subtle)", marginTop: 14 }}>
          {/* The rate with every rule already in it. The rules are not listed:
              a discount and a premium that cancel out read as haggling, and the
              customer wants the number they pay. Pricing still shows all of
              them, and the subtotal is unchanged either way. */}
          <QuoteRow
            label={`${quote.units} × ${quote.rate_unit.replace("_", " ")} at ${formatMoney(effectiveUnitCents)}`}
            value={formatMoney(quote.subtotal_cents)}
          />
          {addonTotal > 0 ? <QuoteRow label={t("reservations.addOns", "Add-ons")} value={formatMoney(addonTotal)} /> : null}

          <div style={{ borderTop: "1px solid var(--rms-border)", marginTop: 8, paddingTop: 8 }}>
            <QuoteRow label={t("reservations.subtotal", "Subtotal")} value={formatMoney(priced?.subtotal_cents ?? grandTotal)} />
          </div>

          {/* One row per configured rate, named as the operator named it and
              carrying its percentage -- which is what somebody querying a bill
              actually asks about. Empty when no tax is set up, rather than a
              zero row that invites the question. */}
          {(priced?.tax_breakdown?.components || []).map((c) => (
            <QuoteRow
              key={c.name + c.sequence}
              label={`${c.name} (${(c.rate_ppm / 10000).toFixed(c.rate_ppm % 10000 ? 3 : 2)}%)`}
              value={formatMoney(c.tax_cents)}
            />
          ))}

          <div style={{ borderTop: "1px solid var(--rms-border-strong)", marginTop: 8, paddingTop: 8 }}>
            <QuoteRow
              label={t("reservations.total", "Total")}
              value={formatMoney(priced?.total_cents ?? grandTotal)}
              strong
            />
          </div>
          {quote.deposit_cents ? (
            <QuoteRow label={t("reservations.depositHeld", "{deposit} held at checkout", { deposit: t("deposit", "Deposit") })} value={formatMoney(quote.deposit_cents)} muted />
          ) : null}
          {!quote.meets_minimum ? (
            // The unit noun is pluralised by ICU, not by appending "s" to a
            // rate unit -- which produced "half_days" in English and was
            // meaningless in the other twelve languages.
            <Banner tone="warn">
              {t("reservations.minimumStay", "Minimum {count} {unit} for these dates.", {
                count: quote.minimum_units,
                unit: t(`rateUnit.plural.${quote.rate_unit}`, quote.rate_unit.replace("_", " "), {
                  count: quote.minimum_units,
                }),
              })}
            </Banner>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * Find a customer by typing, and create one when there is nobody to find.
 *
 * Replaces a <select> listing every customer the business has ever had, which
 * worked at twelve and is unusable at four hundred -- and offered no way at all
 * to take a booking from somebody new without leaving the screen.
 *
 * Matches are shown only once something has been typed, and capped. A list that
 * appears on focus is a dropdown wearing a costume, and one that shows forty
 * names has not narrowed anything.
 */
function CustomerPicker({ customers, value, onPick, onCreate, creating, t, tLower }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selected = customers.find((c) => c.id === value);
  const nameOf = (c) => `${c.first_name} ${c.last_name || ""}`.trim();

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return customers
      .filter((c) => {
        const hay = `${nameOf(c)} ${c.email || ""} ${c.phone || ""} ${c.company_name || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6);
  }, [customers, query]);

  // An exact name match means "create" would make a duplicate, so it is not
  // offered. A near match still is: two people genuinely are called J. Smith.
  const exact = matches.some((c) => nameOf(c).toLowerCase() === query.trim().toLowerCase());
  const canCreate = query.trim().length > 1 && !exact;

  if (selected) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="rms-input" style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
          {nameOf(selected)}
          {selected.is_provisional ? (
            <span className="rms-pill" title={t("reservations.incompleteHint", "Only a name so far.")}>
              {t("reservations.incomplete", "Incomplete")}
            </span>
          ) : null}
          {selected.is_blocked ? <StatusPill status="blocked" /> : null}
        </span>
        <button
          type="button"
          className="rms-btn rms-btn--ghost rms-btn--sm"
          onClick={() => { onPick(""); setQuery(""); setOpen(true); }}
        >
          {t("action.change", "Change")}
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        className="rms-input"
        value={query}
        placeholder={t("reservations.findCustomer", "Type a name…")}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />

      {open && query.trim() ? (
        <div className="rms-card" style={{ position: "absolute", zIndex: 20, insetInlineStart: 0, insetInlineEnd: 0, marginTop: 4, padding: 4 }}>
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              className="rms-btn rms-btn--ghost rms-btn--sm"
              style={{ display: "flex", width: "100%", justifyContent: "flex-start", gap: 8 }}
              onClick={() => { onPick(c.id); setOpen(false); }}
            >
              <span>{nameOf(c)}</span>
              <span style={{ color: "var(--rms-muted-strong)", fontSize: 12 }}>
                {c.email || c.phone || ""}
              </span>
            </button>
          ))}

          {canCreate ? (
            <button
              type="button"
              className="rms-btn rms-btn--sm"
              style={{ display: "flex", width: "100%", justifyContent: "flex-start", marginTop: matches.length ? 4 : 0 }}
              disabled={creating}
              onClick={async () => {
                const made = await onCreate(query);
                if (made) { onPick(made.id); setOpen(false); }
              }}
            >
              {creating
                ? t("common.saving", "Saving…")
                : t("reservations.createCustomer", "Create {name} as a new {customer}", {
                    name: query.trim(),
                    customer: tLower("customer", "customer"),
                  })}
            </button>
          ) : null}

          {!matches.length && !canCreate ? (
            <p style={{ fontSize: 12.5, color: "var(--rms-muted-strong)", padding: "6px 8px", margin: 0 }}>
              {t("reservations.keepTyping", "Keep typing to search or create.")}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function QuoteRow({ label, value, strong, muted, tone }) {
  const colour = tone === "ok" ? "var(--rms-ok)" : tone === "warn" ? "var(--rms-warn)" : undefined;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: muted ? 12.5 : 13.5, padding: "3px 0", color: muted ? "var(--rms-muted-strong)" : colour }}>
      <span>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 550 }}>{value}</span>
    </div>
  );
}

function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default Reservations;
